import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { FileText, Pencil, ShieldCheck, UploadCloud } from "lucide-react";

import { api, openProtected, uploadFile } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Header } from "@/components/header";
import { Badge, Button, Card, ErrorView, Field, Loading, Row } from "@/components/kit";
import { useToast } from "@/components/toast";
import { C, dateTime } from "@/lib/format";

export default function RibScreen() {
  const toast = useToast();
  const qc = useQueryClient();
  const { refresh } = useAuth();
  const q = useQuery({ queryKey: ["my-rib"], queryFn: () => api("/me/rib") });
  const [edit, setEdit] = useState(false);
  const [holder, setHolder] = useState("");
  const [iban, setIban] = useState("");
  const [bic, setBic] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (q.data) {
      setHolder(q.data.holder ?? "");
      setIban(q.data.iban ?? "");
      setBic(q.data.bic ?? "");
      if (!q.data.iban) setEdit(true);
    }
  }, [q.data]);

  const done = async (r, msg) => {
    qc.setQueryData(["my-rib"], r);
    qc.invalidateQueries({ queryKey: ["agent-dashboard"] });
    await refresh();
    toast(msg);
  };

  const save = async (e) => {
    e?.preventDefault();
    setError("");
    if (!holder.trim() || !iban.trim() || !bic.trim()) return setError("Renseignez le titulaire, l'IBAN et le BIC.");
    setSaving(true);
    try {
      const r = await api("/me/rib", { method: "PUT", body: { holder, iban, bic } });
      setEdit(false);
      await done(r, "Coordonnées bancaires enregistrées.");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const uploadPdf = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const f = await uploadFile(file, "rib");
      const r = await api("/me/rib/pdf", { method: "PUT", body: { file_id: f.id } });
      await done(r, "RIB PDF enregistré.");
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setUploading(false);
    }
  };

  const d = q.data;
  return (
    <div data-testid="rib-screen" className="flex flex-col">
      <Header title="Informations bancaires" />
      {q.isLoading ? <Loading /> : q.isError || !d ? <ErrorView message="Erreur de chargement." onRetry={() => q.refetch()} /> : (
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-4 p-4 pb-8 md:p-8">
          <div className="flex flex-wrap items-center gap-3">
            <Badge testID="rib-status-badge" label={d.has_rib ? "RIB renseigné" : "RIB manquant"} color={d.has_rib ? C.success : C.warning} />
            {d.updated_at ? <span className="t-caption">Mis à jour le {dateTime(d.updated_at)}</span> : null}
          </div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-[18px] w-[18px] shrink-0 text-[#27AE60]" aria-hidden />
            <p className="t-caption flex-1">Vos coordonnées sont chiffrées et accessibles uniquement par vous et le gérant autorisé. Chaque consultation est journalisée.</p>
          </div>
          <div className="grid items-start gap-4 lg:grid-cols-2">
            {!edit && d.iban ? (
              <Card>
                <h2 className="t-h3 mb-2">IBAN / BIC</h2>
                <Row label="Titulaire" value={d.holder} testID="rib-holder" />
                <Row label="IBAN" value={d.iban} testID="rib-iban" />
                <Row label="BIC" value={d.bic} testID="rib-bic" />
                <Button testID="rib-edit-button" title="Modifier / remplacer" variant="secondary" icon={Pencil} className="mt-3 w-full" onClick={() => setEdit(true)} />
              </Card>
            ) : (
              <Card>
                <form onSubmit={save} noValidate className="flex flex-col gap-4">
                  <h2 className="t-h3">Saisir mon IBAN et BIC</h2>
                  <Field testID="rib-holder-input" label="Titulaire du compte" value={holder} onChangeText={setHolder} autoComplete="name" />
                  <Field testID="rib-iban-input" label="IBAN" value={iban} onChangeText={setIban} className="uppercase" placeholder="FR76 3000 6000 0112 3456 7890 189" />
                  <Field testID="rib-bic-input" label="BIC" value={bic} onChangeText={setBic} className="uppercase" placeholder="AGRIFRPP" />
                  {error ? <p data-testid="rib-error" role="alert" className="text-sm text-[#EB5757]">{error}</p> : null}
                  <Button testID="rib-save-button" type="submit" title="Enregistrer" loading={saving} />
                  {d.iban ? <Button testID="rib-cancel-button" title="Annuler" variant="ghost" onClick={() => setEdit(false)} /> : null}
                </form>
              </Card>
            )}
            <Card className="flex flex-col gap-3">
              <h2 className="t-h3">RIB au format PDF</h2>
              {d.pdf_file_id ? (
                <Button testID="rib-pdf-view-button" title={d.pdf_name ?? "Voir mon RIB"} icon={FileText} variant="secondary" onClick={() => openProtected(`/files/${d.pdf_file_id}`).catch((e) => toast(e.message, "error"))} />
              ) : (
                <p className="t-caption">Aucun fichier déposé.</p>
              )}
              <input ref={fileRef} type="file" accept="application/pdf" hidden data-testid="rib-pdf-file-input" onChange={(e) => { uploadPdf(e.target.files?.[0]); e.target.value = ""; }} />
              <Button testID="rib-pdf-upload-button" title={d.pdf_file_id ? "Remplacer le PDF" : "Ajouter mon RIB (PDF)"} icon={UploadCloud} loading={uploading} onClick={() => fileRef.current?.click()} />
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
